import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Divider, Flex, HStack, Icon, IconButton, SimpleGrid, Text, VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  BarChart3, ClipboardList, Hourglass, Laptop, RefreshCw, UserPlus, Users, Zap,
  type LucideIcon,
} from "lucide-react";

import { ErrorState } from "../../components/ErrorState";
import { OnflyLoading } from "../../tools/OnflyLoading";
import { getMetricsDashboard, type MetricsDashboard } from "../../lib/api";
import { getToken } from "../../lib/auth";
import { MetricCard } from "./components/MetricCard";
import { MetricChartCard } from "./components/MetricChartCard";
import { DateRangeFilter, type DateRange } from "./components/DateRangeFilter";

const SECTION_ICONS: Record<string, LucideIcon> = {
  onboarding: UserPlus,
  activation: Zap,
  bottlenecks: Hourglass,
  hr_process: ClipboardList,
  hr_engagement: Users,
  integrations: Laptop,
  internal_adoption: BarChart3,
};

const toYmd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const currentMonthRange = (): DateRange => {
  const now = new Date();
  return { startDate: new Date(now.getFullYear(), now.getMonth(), 1), endDate: now };
};

const fmtRange = (iso: string): string => {
  const [y, m, d] = iso.split("-");
  return d ? `${d}/${m}/${y}` : `${m}/${y}`;
};

/** Cabeçalho de seção — ícone em bloco arredondado + título, mesmo padrão
 * usado em WhitelistSection ("Controle de acesso"). */
const SectionHeader = ({ icon, label }: { icon: LucideIcon; label: string }) => {
  const iconBg = useColorModeValue("blue.50", "rgba(0,158,251,0.1)");
  return (
    <HStack spacing={3}>
      <Flex align="center" justify="center" w="40px" h="40px" borderRadius="xl" bg={iconBg} flexShrink={0}>
        <Icon as={icon} boxSize={5} color="blue.500" />
      </Flex>
      <Text fontWeight={700} fontSize="sm">{label}</Text>
    </HStack>
  );
};

export const MetricsPage = () => {
  const [range, setRange] = useState<DateRange>(currentMonthRange);
  const [data, setData] = useState<MetricsDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const muted = useColorModeValue("gray.500", "gray.400");
  const dividerColor = useColorModeValue("gray.100", "whiteAlpha.100");

  const token = getToken() ?? "";
  const dateFrom = useMemo(() => toYmd(range.startDate), [range.startDate]);
  const dateTo = useMemo(() => toYmd(range.endDate), [range.endDate]);

  const load = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setIsLoading(true);
    setHasError(false);
    try {
      setData(await getMetricsDashboard(token, dateFrom, dateTo));
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [token, dateFrom, dateTo]);

  useEffect(() => { void load(); }, [load]);

  if (isLoading && !data) {
    return (
      <Flex minH="60vh" align="center" justify="center">
        <OnflyLoading />
      </Flex>
    );
  }

  if (hasError && !data) {
    return <ErrorState onRetry={() => void load()} />;
  }

  const meta = data?.sections_meta ?? [];

  return (
    <Box maxW="container.xl" mx="auto" p={{ base: 4, md: 6 }}>
      <Flex
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={3}
        mb={5}
        pb={4}
        borderBottomWidth="1px"
        borderColor={dividerColor}
      >
        {data && (
          <Text fontSize="sm" color={muted}>
            {fmtRange(data.range.start)} a {fmtRange(data.range.end)} · comparado com{" "}
            {fmtRange(data.range.previous_start)} a {fmtRange(data.range.previous_end)}
          </Text>
        )}

        <HStack spacing={2}>
          <DateRangeFilter value={range} onChange={setRange} />
          <IconButton
            aria-label="Atualizar"
            title="Atualizar"
            icon={<RefreshCw size={14} />}
            size="sm"
            variant="outline"
            onClick={() => void load(true)}
            isLoading={isLoading}
          />
        </HStack>
      </Flex>

      <VStack align="stretch" spacing={7}>
        {meta.map((s, idx) => {
          const section = data?.sections?.[s.key];
          const charts = Object.entries(section?.charts ?? {});
          return (
            <Box key={s.key}>
              {idx > 0 && <Divider borderColor={dividerColor} mb={7} />}
              <SectionHeader icon={SECTION_ICONS[s.key] ?? BarChart3} label={s.label} />

              <Box mt={4}>
                {section?.error ? (
                  <Text fontSize="sm" color="red.400">
                    Não foi possível carregar esta seção: {section.error}
                  </Text>
                ) : (
                  <>
                    {!!section?.cards.length && (
                      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3} mb={4}>
                        {section.cards.map((c) => (
                          <MetricCard key={c.key} card={c} />
                        ))}
                      </SimpleGrid>
                    )}
                    <SimpleGrid columns={{ base: 1, xl: charts.length > 1 ? 2 : 1 }} spacing={4}>
                      {charts.map(([key, chart]) => (
                        <MetricChartCard key={key} chart={chart} />
                      ))}
                    </SimpleGrid>
                  </>
                )}
              </Box>
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
};
